
import { useState } from "react"
import { Users, Plus, Search, Clock, CheckCircle, User, Truck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Layout } from "@/components/Layout"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { useVisitors, useUpdateVisitorExit } from "@/hooks/useVisitors"
import RegistrarVisitanteModal from "@/components/RegistrarVisitanteModal"
import { Loader2 } from "lucide-react"
import { format } from "date-fns"

const Visitantes = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const { data: visitors = [], isLoading } = useVisitors()
  const { mutate: registerExit } = useUpdateVisitorExit()

  const filteredVisitors = visitors.filter(visitor =>
    visitor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    visitor.unit?.number.includes(searchTerm) ||
    visitor.document?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    visitor.vehicle_plate?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const todayVisitors = visitors.filter(visitor => {
    const today = new Date().toDateString()
    const visitDate = new Date(visitor.visit_date || visitor.created_at).toDateString()
    return today === visitDate
  })

  const currentlyInside = visitors.filter(visitor => 
    visitor.entry_time && !visitor.exit_time && visitor.status === "approved"
  )

  const motoboyStats = visitors.filter(visitor => visitor.visitor_type === "delivery")

  const getStatusColor = (status: string, hasExited: boolean) => {
    if (hasExited) {
      return "bg-gray-100 text-gray-800 border-gray-200"
    }
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-200"
      case "waiting":
        return "bg-yellow-100 text-yellow-800 border-yellow-200"
      case "denied":
        return "bg-red-100 text-red-800 border-red-200"
      default:
        return "bg-gray-100 text-gray-800 border-gray-200"
    }
  }

  const getStatusText = (status: string, hasExited: boolean) => {
    if (hasExited) return "Saiu"
    switch (status) {
      case "approved":
        return "Dentro"
      case "waiting":
        return "Aguardando"
      case "denied":
        return "Negado"
      default:
        return status
    }
  }

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case "guest":
        return <User className="h-4 w-4" />
      case "delivery":
        return <Truck className="h-4 w-4" />
      case "service":
        return <Users className="h-4 w-4" />
      default:
        return <User className="h-4 w-4" />
    }
  }

  const getTipoText = (tipo: string) => {
    switch (tipo) {
      case "guest":
        return "Visitante"
      case "delivery":
        return "Motoboy"
      case "service":
        return "Serviço"
      default:
        return "Outro"
    }
  }

  const handleRegisterExit = (visitorId: string) => {
    registerExit(visitorId)
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Controle de Visitantes</h1>
            <p className="text-gray-600 dark:text-gray-400">Gerencie a entrada e saída de visitantes e motoboys</p>
          </div>
          <RegistrarVisitanteModal />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Visitantes Hoje</p>
                  <p className="text-2xl font-bold">{todayVisitors.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Dentro do Condomínio</p>
                  <p className="text-2xl font-bold">{currentlyInside.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Truck className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Motoboys Total</p>
                  <p className="text-2xl font-bold">{motoboyStats.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total de Visitantes</p>
                  <p className="text-2xl font-bold">{visitors.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input 
              placeholder="Buscar por nome, unidade, documento ou placa..." 
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-4">
          {filteredVisitors.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Nenhum visitante encontrado
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {searchTerm ? "Tente ajustar sua busca" : "Ainda não há visitantes registrados"}
                </p>
                <RegistrarVisitanteModal>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Registrar Primeira Entrada
                  </Button>
                </RegistrarVisitanteModal>
              </CardContent>
            </Card>
          ) : (
            filteredVisitors.map((visitor) => (
              <Card key={visitor.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center space-x-4">
                      <Avatar>
                        <AvatarFallback className="bg-blue-100 text-blue-700">
                          {getTipoIcon(visitor.visitor_type || "guest")}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-gray-900 dark:text-white">{visitor.name}</h3>
                          <Badge variant="outline" className={getStatusColor(visitor.status || "waiting", !!visitor.exit_time)}>
                            {getStatusText(visitor.status || "waiting", !!visitor.exit_time)}
                          </Badge>
                          <Badge variant="secondary">
                            {getTipoText(visitor.visitor_type || "guest")}
                          </Badge>
                        </div>
                        
                        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                          {visitor.document && (
                            <p>CPF/Doc: {visitor.document}</p>
                          )}
                          {visitor.vehicle_plate && (
                            <p>Placa: {visitor.vehicle_plate}</p>
                          )}
                          <p>
                            {visitor.unit ? `Unidade ${visitor.unit.number}` : "Sem unidade"}
                          </p>
                        </div>
                        
                        {visitor.notes && (
                          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                            Obs: {visitor.notes}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <div className="text-sm">
                        {visitor.entry_time && (
                          <div className="flex items-center space-x-1 text-gray-600">
                            <Clock className="h-3 w-3" />
                            <span>Entrada: {format(new Date(visitor.entry_time), "HH:mm")}</span>
                          </div>
                        )}
                        {visitor.exit_time && (
                          <div className="flex items-center space-x-1 text-gray-600">
                            <CheckCircle className="h-3 w-3" />
                            <span>Saída: {format(new Date(visitor.exit_time), "HH:mm")}</span>
                          </div>
                        )}
                      </div>
                      
                      {visitor.entry_time && !visitor.exit_time && visitor.status === "approved" && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleRegisterExit(visitor.id)}
                        >
                          Registrar Saída
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  )
}

export default Visitantes
